<template>
  <v-card flat tile>
    <v-toolbar flat tile dense>
      <v-toolbar-title>Location Map ({{ totalWells | numberFormat }} wells)</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-autocomplete v-if="hasName" :items="wellList" v-model="selectedWell" label="" hide-details clearable prepend-inner-icon="mdi-api" />
      <v-autocomplete v-else :items="wellList" v-model="selectedWell" label="" hide-details clearable prepend-inner-icon="mdi-api" />
      <ax-menu
        icon="mdi-function-variant"
        title="Color feature"
        :options="colorOptions"
        :default="color.value"
        model="color.value"
        @changed="onMenuChange"
      ></ax-menu>
    </v-toolbar>
    <div ref="mapContainer"></div>
  </v-card>
</template>

<script lang="js">
import toolbar from "@/components/toolbar"
import Plotly from 'plotly.js-dist-min'
import AxMenu from "@/components/menu"
import { getCoverage } from "@/utils/coverage"

// Continental US, used before wells load or when none have coordinates
const DEFAULT_VIEW = Object.freeze({ lat: 38, lon: -98, zoom: 3.5 })

export default {
  name: 'WellMap',
  props: { 
    wells: { type: Array, required: true },
    config: { type: Object, required: true },
    target: { type: String, required: true },
    well: { type: String, default: null },
    group: { type: Object, default: () => {} },
  },
  components: {
    toolbar,
    AxMenu
  },
  data() {
    return {
      selectedWell: null,
      color: {
        value: "production",
        options: [
          { text: "Operator", value: "operator" },
          { text: "Length of Production", value: "duration" },
          { text: "Total Production", value: "production" },
          { text: "Total EUR", value: "eur" },
        ],
      },
      mapReadyPromise: null,
      targets: [
        { text: "Oil", value: "oil", units: "bbl", color: "#4CAF50", code: 'green' },
        { text: "Gas", value: "gas", units: "mcf", color: "#F44336", code: 'red' },
        { text: "Water", value: "water", units: "bbl", color: "#03A9F4", code: 'light-blue' },
      ],
      chart: {
        data: [],
        layout: {
          autosize: true,
          hovermode: "closest",
          showlegend: false,
          height: 500,
          map: {
            style: "open-street-map",
            bearing: 0,
            center: {
              lat: DEFAULT_VIEW.lat,
              lon: DEFAULT_VIEW.lon,
            },
            pitch: 0,
            zoom: DEFAULT_VIEW.zoom,
          },
          font: {
            family: "Roboto, sans-serif",
            color: "#808080",
          },
          legend: {
            x: 0,                      // Move it inside the map view area if needed
            y: 0,
            bgcolor: 'rgba(255, 255, 255, 0.8)' // Give it a background so it stands out
          },
          margin: { l: 0, r: 0, b: 0, t: 0, pad: 0 },
          paper_bgcolor: "rgba(0,0,0,0)",
          plot_bgcolor: "rgba(0,0,0,0)",
          title: {
            text: null,
            x: 0.5,
            y: 0.01,
            automargin: true,
          },
          template: {},
          coloraxis: {},
          layout: {}
        },
        options: {
          displaylogo: false,
          responsive: true,
        }
      },
      marker: {
        selected: {
          size: 12,
          opacity: 1.0,
          color: '#F44336'
        },
        unselected: {
          opacity: 0.1
        },
      },
      countiesGeojson: null,
      countyFipsCodes: [],
      dummyZData: [],
      countyLabelsTrace: null ,
    }
  },
  async mounted() {
    this.initializeMap()
    this.fetchCounties()
    this.plotCoverage()
  },
  computed: {
    hasName() {
      if (!!this.config) {
        return 'name' in this.config && this.config.name
      }
    },
    wellList() {
      if (!this.isEmpty(this.wells)) {
        if (this.hasName) {
          return Array.from(this.wells, (well => {
            return { text: well[this.config.name], value: well[this.config.api] }
          }))
        }
        return Array.from(this.wells, (well => {
          return { text: well[this.config.api], value: well[this.config.api] }
        }))
      }
    },
    totalWells() {
      if (!this.isEmpty(this.wells)) {
        return this.wells.map(x => x[this.config.api]).length
      }
      return 0
    },
    targetUnits() {
      let units = undefined
      units = this.targets.find((t) => t.value == this.target).units
      return units
    },
    // The map data only has an operator column when "Current Operator" was mapped
    hasOperator() {
      return !this.isEmpty(this.wells) && 'CurrentOperator' in this.wells[0]
    },
    colorOptions() {
      return this.color.options.filter(o => o.value !== 'operator' || this.hasOperator)
    },
    colorColumn() {
      if (this.color.value == "production") return this.target
      if (this.color.value == "duration") return "Months"
      if (this.color.value == "eur") return "EUR"
      if (this.color.value == "Length") return "Length"
      if (this.color.value == "operator") return "CurrentOperator"
      return this.target
    },
    mapObject() {
      const precision = 6 // Number of decimal places
      const factor = Math.pow(10, precision) // 100

      this.wells.forEach(x => {
        x[this.config.api] = String(x[this.config.api])
        x[this.config.latitude] = Math.round(x[this.config.latitude] * factor) / factor
        x[this.config.longitude] = Math.round(x[this.config.longitude] * factor) / factor
      })

      // In this order for now
      // [API10, WellName, SHLLatitude, SHLLongitude, Year, CurrentOperator, Length, oil, gas, water, Months, EUR]
      if (this.color.value == 'operator' && this.hasOperator) {
        return this.nominalMapObject
      }
      return this.scalarMapObject
    },
    scalarMapObject() {
      return [{
        name: 'wells',
        type: "scattermap",
        text: this.wells.map(x => x[this.config.api]),
        ...this.hoverData(this.wells),
        lat: this.wells.map(x => x[this.config.latitude]),
        lon: this.wells.map(x => x[this.config.longitude]),
        mode: "markers",
        marker: {
          size: 6,
          reversescale: true,
          autocolorscale: false,
          color: this.wells.map(x => x[this.colorColumn]),
          colorscale: 'Portland',
          cmin: Math.min(...this.wells.map(x => x[this.colorColumn])),
          cmax: Math.max(...this.wells.map(x => x[this.colorColumn])),
          colorbar: {
            thickness: 10,
            titleside: "right",
            outlinecolor: "rgba(0,0,0,0)",
            ticks: "outside",
            ticklen: 3,
            x: 0.9,
            y: 0.5,
            bgcolor: "rgba(0,0,0,0)",
          },
        },
        selected: {
          marker: Object.freeze(this.marker.selected)
        },
        unselected: {
          marker: Object.freeze(this.marker.unselected)
        }
      }]
    },
    nominalMapObject() {
      const uniqueCategories = [...new Set(this.wells.map(x => x['CurrentOperator']))]
      const traces = []
      uniqueCategories.forEach((cat, index) => {
        const data = this.wells.filter(x => x['CurrentOperator'] == cat)

        const hue = (index * (360 / data.length)) % 360 
        const autoColor = `hsl(${hue}, 70%, 50%)`

        const trace = {
          name: cat,
          type: "scattermap",
          text: data.map(x => x[this.config.api]),
          ...this.hoverData(data),
          lat: data.map(x => x[this.config.latitude]),
          lon: data.map(x => x[this.config.longitude]),
          mode: "markers",
          showlegend: true,
          marker: {
            size: 6,
            color: autoColor,
          },
          selected: {
            marker: Object.freeze(this.marker.selected)
          },
          unselected: {
            marker: Object.freeze(this.marker.unselected)
          },
          subplot: 'map'
        }
        traces.push(trace)
      })
      return traces
    },
    countyMapObject() {
      if (!this.countiesGeojson) return []
      return [{
        name: 'shapes',
        type: 'choroplethmap',
        geojson: this.countiesGeojson,
        locations: this.countyFipsCodes,
        z: this.dummyZData,
        showscale: false,
        colorscale: [[0, 'rgba(0,0,0,0)'], [1, 'rgba(0,0,0,0)']], 
        marker: {
          line: { color: this.countyLineColor() }
        },
        hoverinfo: 'skip'
      }]
    }
  },
  watch: {
    wells: function(val) {
      if (val) {
        // Switched to a prediction without operators while colouring by operator;
        // the color.value watcher replots
        if (this.color.value == 'operator' && !this.hasOperator) {
          this.color.value = 'production'
          return
        }
        this.plotMarkers()
      }
    },
    // 'chart.data': {
    //   deep: true,
    //   handler(newData) {
    //     this.updatePlotly(newData, this.chart.layout)
    //   }
    // },
    // 'chart.layout': {
    //   deep: true,
    //   handler(newLayout) {
    //     this.updatePlotly(this.chart.data, newLayout)
    //   }
    // },
    selectedWell: function(val) {
      if (val) {
        this.selectPoints([val])
      } else {
        this.selectPoints()
      }
      this.$emit('selected', val)
    },
    well: function(val) {
      this.selectedWell = val
    },
    group: {
      handler(val) {
        this.selectPoints()
        if (val) {
          if (!this.isEmpty(this.group.wells)) {
            this.selectPoints(this.group.wells)
          } else {
            this.selectPoints(this.wells.map(x => x[this.config.api]))
          }
        }
      }, deep: true 
    },
    "color.value": async function (val, old) {
      let column = null
      let units = null

      if (val == "production") column = this.target
      if (val == "duration") column = "Months"
      if (val == "eur") column = "EUR"
      if (val == "Length") column = "Length"
      if (val == "operator") column = "CurrentOperator"

      if (val == "production") units = this.targetUnits
      if (val == "duration") units = "Months"
      if (val == "eur") units = this.targetUnits
      if (val == "Length") units = "Feet"
      if (val == "operator") units = ""

      this.chart.layout.showlegend = false
      this.chart.layout.title.text = this.color.options.find((x) => x.value == this.color.value).text
      if (val !== 'operator') {
        this.chart.layout.title.text = this.chart.layout.title.text.concat(` (in ${units})`)
      }

      if (val != 'operator') {
        if (old == 'operator') {
          this.plotMarkers()
          this.chart.layout.showlegend = false
        } else {
          const min_max = this.wells.map(x => x[column])
          const traceIndex = this.chart.data.findIndex(x => x.name === 'wells')
          if (traceIndex !== -1) {
            const trace = this.chart.data[traceIndex]
            trace.marker.cmin = Math.min(...min_max)
            trace.marker.cmax = Math.max(...min_max)
            trace.marker.color = this.wells.map(x => x[column]).slice()
      
            const gd = await this.mapReadyPromise
            if (gd) {
              Plotly.react(gd, this.chart.data, this.chart.layout)
            }
          }
        }
      } else {
        this.plotMarkers()
        this.chart.layout.showlegend = true
      }
    },
    isDark: function (val) {
      this.toggleDarkMode(val)
      this.styleOverlays()
    },
  },
  methods: {
    // Builds the well tooltip and its customdata by column name. The map data only
    // has the optional columns (name, operator, each target) that this prediction
    // mapped, so reading them by position would shift the labels.
    hoverData(rows) {
      const sample = this.wells[0] || {}
      const fields = []
      const field = (key) => {
        fields.push(key)
        return `%{customdata[${fields.length - 1}]}`
      }

      const lines = ["%{text}"]
      if (this.hasName) lines.push(field(this.config.name))
      if ('CurrentOperator' in sample) lines.push(`Operator: ${field('CurrentOperator')}`)
      lines.push(`Started: ${field('Year')}, Age: ${field('Months')} months`)
      const production = this.targets.filter(t => t.value in sample).map(t => `${t.text}: ${field(t.value)}`)
      if (production.length) lines.push(production.join(", "))

      return {
        customdata: rows.map(row => fields.map(key => row[key])),
        hovertemplate: lines.join("<br>") + "<extra></extra>",
      }
    },
    initializeMap() {
      const container = this.$refs.mapContainer
      
      // Save the Plotly instantiation promise to our data layer
      this.mapReadyPromise = Plotly.newPlot(container, this.chart.data, this.chart.layout)
        .then((gd) => {
          container.on('plotly_click', this.onMapClick)
          container.on('plotly_selected', this.onMapSelect)
          gd.on('plotly_afterplot', () => {
            // 
          })
          return gd // Pass the DOM node downward into the chain
        })
        .catch((err) => {
          console.error("Mapbox initialization error:", err)
        })
    },
    async updatePlotly(newData, newLayout) {
      // 1. Force updates to wait for the initialization promise to resolve!
      // This completely solves the "this.chart.getStyle() is undefined" race conditions.
      const gd = await this.mapReadyPromise
      
      if (gd) {
        // 2. Use Plotly.react for highly efficient incremental UI draws
        await Plotly.react(gd, newData, newLayout)
      }
    },
    async fetchCounties() {
      const response = await fetch('https://raw.githubusercontent.com/plotly/datasets/master/geojson-counties-fips.json')
      const geojsonData = await response.json()
      
      this.countiesGeojson = geojsonData
      this.countyFipsCodes = geojsonData.features.map(f => f.id)
      this.dummyZData = new Array(this.countyFipsCodes.length).fill(0)

      // Calculate center coordinates and grab the county name for labels
      const lats = []
      const lons = []
      const names = []

      geojsonData.features.forEach(feature => {
        let latSum = 0, lonSum = 0, count = 0
        const geom = feature.geometry

        // Handle both Polygon and MultiPolygon structures safely
        if (geom.type === 'Polygon') {
          geom.coordinates[0].forEach(coord => {
            lonSum += coord[0]
            latSum += coord[1]
            count++
          })
        } else if (geom.type === 'MultiPolygon') {
          geom.coordinates.forEach(polygon => {
            polygon[0].forEach(coord => {
              lonSum += coord[0]
              latSum += coord[1]
              count++
            })
          })
        }

        if (count > 0) {
          lons.push(lonSum / count)
          lats.push(latSum / count)
          // Extract county name (e.g., "Autauga" or "Los Angeles") from GeoJSON properties
          names.push(feature.properties.NAME || "") 
        }
      })

      // Create the dedicated text label trace
      this.countyLabelsTrace = {
        name: 'labels',
        type: 'scattermap',
        mode: 'text',
        lon: lons,
        lat: lats,
        text: names,
        hoverinfo: 'skip',
        showlegend: false,
        textposition: 'middle center',
        textfont: {
          size: 8,
          color: '#666', // Subtle gray so it doesn't clash with data points
        }
      }

      const gd = await this.mapReadyPromise
      if (gd) {
        if (!this.isEmpty(this.countyMapObject)) {
          Plotly.addTraces(gd, ...this.countyMapObject)
          Plotly.addTraces(gd, this.countyLabelsTrace)
        } else {
          console.error('Empty county map object')
        }
      }
    },
    async plotMarkers() {
      // Populate map
      const gd = await this.mapReadyPromise
      if (gd) {
        // Delete existing markers
        const traces = this.chart.data.flatMap((trace, index) => ['shapes','labels','coverage'].includes(String(trace.name)) ? [] : index)
        Plotly.deleteTraces(gd, traces)

        // Set map style
        this.chart.layout.map.style = this.isDark ? 'dark' : 'light'
  
        const lats = this.wells.map(x => x[this.config.latitude]).filter(x => x !== 0)
        const lons = this.wells.map(x => x[this.config.longitude]).filter(x => x !== 0)

        if (lats.length && lons.length) {
          // Center align and zoom map
          this.chart.layout.map.center.lat = this.mean(lats)
          this.chart.layout.map.center.lon = this.mean(lons)
          this.chart.layout.map.zoom = this.mapZoom(lats, lons)
        } else {
          // No located wells (mean/zoom would be NaN and land at 0,0), so show the whole US
          this.chart.layout.map.center.lat = DEFAULT_VIEW.lat
          this.chart.layout.map.center.lon = DEFAULT_VIEW.lon
          this.chart.layout.map.zoom = DEFAULT_VIEW.zoom
        }
  
        // Specify label and units
        this.chart.layout.title.text = this.color.options.find((x) => x.value == this.color.value).text
        if (this.color.value !== 'operator') {
          this.chart.layout.title.text = this.chart.layout.title.text.concat(` (in ${this.targetUnits})`)
        }

        Plotly.addTraces(gd, this.mapObject)
      }
    },
    colorMarkers() {

    },
    async plotCoverage() {
      let coverage
      try {
        coverage = await getCoverage()
      } catch (err) {
        console.error(err)
        return
      }

      const gd = await this.mapReadyPromise
      if (gd) {
        // The cached coverage is frozen and shared between maps, so hand Plotly
        // shallow copies it can annotate. The heavy coordinate arrays and GeoJSON
        // sources stay frozen, which also keeps Vue from making them reactive.
        // Center/zoom/bounds from its layout are left to plotMarkers.
        //
        // The hex fills come as layout.map.layers, but Plotly drops layout layers
        // whenever the map style changes (light/dark) and never re-adds them, so
        // they are drawn as choroplethmap traces instead, which survive a restyle.
        // Each layer holds one merged shape per basin (scripts/build-coverage.js),
        // so there are no shared hexagon edges to show through the fill.
        // `meta` keeps the file's light-mode colour so styleOverlays can restore it
        const fills = coverage.layout.map.layers.map(layer => ({
          name: 'coverage',
          type: 'choroplethmap',
          meta: { color: layer.color },
          geojson: layer.source,
          locations: Object.freeze(layer.source.features.map(f => f.id)),
          z: Object.freeze(new Array(layer.source.features.length).fill(0)),
          showscale: false,
          marker: {
            opacity: layer.opacity,
            line: { width: 0, color: layer.fill.outlinecolor },
          },
          hoverinfo: 'skip',
        }))
        const outlines = coverage.data.map(trace => ({
          ...trace,
          name: 'coverage',
          meta: { color: trace.line.color },
          line: { ...trace.line },
        }))
        const traces = [...fills, ...outlines]
        traces.forEach(this.colorCoverageTrace)

        Plotly.addTraces(gd, traces)
      }
    },
    // Recolours the county and coverage overlays for the current light/dark mode
    async styleOverlays() {
      const gd = await this.mapReadyPromise
      if (gd) {
        this.chart.data.forEach(trace => {
          if (trace.name === 'coverage') this.colorCoverageTrace(trace)
          if (trace.name === 'shapes') trace.marker = { ...trace.marker, line: { ...trace.marker.line, color: this.countyLineColor() } }
        })
        Plotly.react(gd, this.chart.data, this.chart.layout)
      }
    },
    colorCoverageTrace(trace) {
      // The file's dark greys disappear on the dark basemap, so swap to white
      if (trace.type === 'choroplethmap') {
        const color = this.isDark ? '#FFFFFF' : trace.meta.color
        trace.colorscale = [[0, color], [1, color]]
      } else {
        trace.line = { ...trace.line, color: this.isDark ? 'rgba(255, 255, 255, 0.8)' : trace.meta.color }
      }
    },
    countyLineColor() {
      // Faint in dark mode so the white coverage outlines stand out
      return this.isDark ? 'rgba(255, 255, 255, 0.15)' : '#ccc'
    },
    mapZoom(lats, lons) {
      const x1 = Math.max(...lats)
      const x2 = Math.min(...lats)

      const y1 = Math.max(...lons)
      const y2 = Math.min(...lons)

      // At least 1 km so a single well doesn't give log(0) = -Infinity
      const max_bound = Math.max(Math.abs(x1 - x2), Math.abs(y1 - y2), 1 / 111) * 111
      const zoom = 12.5 - Math.log(max_bound)
      return zoom
    },
    onMenuChange(value, variable) {
      this.setPath(this, variable, value)
    },
    async selectPoints(wells = []) {
      const gd = await this.mapReadyPromise

      if (!this.isEmpty(this.chart.data)) {
        const traceIndex = this.chart.data.findIndex(x => x.name === 'wells')
        
        if (traceIndex !== -1) {
          const trace = this.chart.data[traceIndex]
          delete trace.selectedpoints 
          
          if (!this.isEmpty(wells) && trace.text) {
            const targetIndices = trace.text
              .map((e, i) => (wells.includes(e) ? i : ""))
              .filter(val => val !== "")

            trace['selectedpoints'] = targetIndices
          } 
          if (gd) {
            Plotly.react(gd, this.chart.data, this.chart.layout)
          }
        }
      }
    },
    onMapClick(event) {
      if (!!event) {
        let well = event.points[0].text
        if (well == this.selectedWell) {
          this.selectedWell = null
        } else {
          this.selectedWell = well
        }
      }
    },
    onMapSelect(event) {
      if (!!event) {
        if (!this.isEmpty(event.points)) {
          let wells = event.points.map((p) => p.text).filter(w => !!w)
          if (wells.length > 0) {
            this.$emit('created', wells)
          }
        }
      }
    },
    onMapDoubleClick(event) {
      this.selectPoints()
    },
    toggleDarkMode(val) {
      if (val) {
        this.chart.layout.map.style = "dark"
      } else {
        this.chart.layout.map.style = "light"
      }
    },
  },
  beforeDestroy() {
    // Clean up memory allocations and event hooks when the component unmountes
    if (this.$refs.mapContainer) {
      const chartDiv = this.$refs.mapContainer
      chartDiv.removeEventListener('plotly_click', this.onMapClick)
      chartDiv.removeEventListener('plotly_selected', this.onMapSelect)
      Plotly.purge(this.$refs.mapContainer)
    }
  },
}
</script>